import { keyBy } from 'lodash/fp';
import { useEffect } from 'react';

import { useJavascriptResolver } from 'dashboard/components/GridLayoutRender/computed-properties/useJavascriptResolver';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { CaseDataManager } from 'dashboard/models/case';
import {
  CASE_QUERY_RESULT_TERMINAL_STATUSES,
  CaseQueryResult,
  CaseQueryResultStatus,
} from 'dashboard/models/case-query-result';
import { ModuleTable } from 'dashboard/modules/Table/model';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';

export interface TableProps {
  module: ModuleTable;
  caseDataManager: CaseDataManager;
  caseQueryResults: CaseQueryResult[];
}

const Table = ({ module, caseDataManager, caseQueryResults }: TableProps) => {
  const caseQueryResultsContext = keyBy(
    (caseQueryResult) => caseQueryResult.attributes.name,
    caseQueryResults,
  );

  // TODO(zixiao): add case query result to case data manager
  const [isLoadingJavascriptResolver, resolvedData, resolverError] = useJavascriptResolver(
    module.data,
    {
      ...caseQueryResultsContext,
      caseDataManager,
    },
  );

  // Send resolverError message to console.
  useEffect(() => {
    if (!resolverError) {
      return;
    }

    console.error(resolverError.stack);
  }, [resolverError]);

  // check if all dependencies are in terminal states
  const isLoadingCaseQuery = !module['case-query-dependencies'].every((queryName) =>
    CASE_QUERY_RESULT_TERMINAL_STATUSES.includes(
      caseQueryResultsContext[queryName]?.attributes?.status,
    ),
  );

  if (isLoadingJavascriptResolver || isLoadingCaseQuery) {
    return (
      <Card data-test={`module__table--${module.token}`}>
        <Card.Body>
          <Card.Title>{module.title}</Card.Title>
          <LoadingIndicator />
        </Card.Body>
      </Card>
    );
  }

  const isQueryErrored = module['case-query-dependencies'].some(
    (queryName) =>
      caseQueryResultsContext[queryName]?.attributes?.status === CaseQueryResultStatus.Errored,
  );

  // handle error when case query result is failed to resolve
  let resolvedTableData;
  let JSONParseError: Error;
  try {
    resolvedTableData = resolvedData != null ? JSON.parse(resolvedData) : {};
  } catch (e) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    JSONParseError = e;
    console.warn('Debug information for failed parse of table data: ');
    /* eslint-disable no-console */
    if (resolvedData == null) {
      console.group('Table Data');
    } else if (resolvedData.split('\n').length > 10 || resolvedData.length > 500) {
      console.groupCollapsed('Table Data');
    } else {
      console.group('Table Data');
    }
    console.log(resolvedData);
    console.groupEnd();
    /* eslint-enable no-console */
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (resolverError || JSONParseError || isQueryErrored) {
    return (
      <Card data-test={`module__table--${module.token}`}>
        <Card.Body>
          <Card.Title>{module.title}</Card.Title>
          <Paragraph color='tertiary'>
            Failed to resolve table data, please update the data in the case template settings.
          </Paragraph>
        </Card.Body>
      </Card>
    );
  }

  const { header, rows } = resolvedTableData;

  const tableHeaders = (header || []).map((headerName: string) => {
    return <TableTH key={`module__table-header--${headerName}`}>{headerName}</TableTH>;
  });

  const tableRows = ((rows as string[][]) || []).map((row: string[], rowIdx) => {
    return (
      <TableTR key={`module__table-row--${rowIdx}`}>
        {row.map((cell: string, columnIdx) => {
          return <TableTD key={`module__table-cell--${rowIdx}-${columnIdx}`}>{cell}</TableTD>;
        })}
      </TableTR>
    );
  });

  return (
    <Card data-test={`module__table--${module.token}`}>
      <Card.Body>
        <Card.Title>{module.title}</Card.Title>
        <TableLayout
          isEmpty={tableRows.length === 0}
          headerTR={<>{tableHeaders}</>}
          nullState={
            <TableNullState dataTestBlock='module__table--null-state'>No rows found</TableNullState>
          }
        >
          {tableRows}
        </TableLayout>
      </Card.Body>
    </Card>
  );
};

export default Table;
